// libs.
import { Badge, Col, Row } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import ListGroup from 'react-bootstrap/ListGroup';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AutocompleteFieldItem } from '@domains/documents-review/interfaces/interfaces';
import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import { CountryCode } from '@domains/country';
import { applyAutocompleteSuggestion } from '@domains/documents-review/store/slices/document-review-slice';
import { getFieldConfig } from '@domains/documents-review/config/fieldConfigs';
import { EditableFieldByType } from './EditableFieldByType';

// styles.
import { WrapperModal } from '@pages/DocumentAuditPage/modules/ReassignDocumentsTask/components/styles';

//utils.
import {
  getFieldLabelForCountry,
  SOURCE_LABELS,
  SOURCE_TYPE_LABELS,
  MODEL_LABELS,
  getFieldLabel,
} from '@domains/documents-review/utils/autocompleteLabels';
import { getConfidenceBadgeVariant } from './utils';
import { DocumentBySource } from './DocumentBySource';
import { FileViewer } from '@pages/DocumentAuditPage/modules/DocumentTaskDetail/components/DocumentsViewer/components/FileViewer';

interface AutocompleteCardProps {
  item: AutocompleteFieldItem;
}

export const AutocompleteCard: React.FC<AutocompleteCardProps> = ({ item }) => {
  const dispatch = useDispatch();
  const country = useSelector(getCountry) as CountryCode;

  const [show, setShow] = useState(false);
  const [docToView, setDocToView] = useState<string | null>(null);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [comment, setComment] = useState<string>('');
  const [editedValue, setEditedValue] = useState<string>('');

  const title = useMemo(
    () => getFieldLabelForCountry(country, item.field),
    [country, item.field]
  );

  const handleOpen = (idx: number) => {
    setSelectedIdx(idx);
    setComment('');
    setEditedValue(item.suggestions[idx].suggestedValue);
    setShow(true);
  };

  useEffect(() => {
    if (selectedIdx !== null && item.suggestions[selectedIdx]) {
      setEditedValue(item.suggestions[selectedIdx].suggestedValue);
    }
  }, [selectedIdx, item.suggestions]);

  const handleAccept = () => {
    if (selectedIdx == null) return;
    const sug = item.suggestions[selectedIdx];

    const valueToUse =
      item.fieldConfidenceLevel === 'HIGH' ? editedValue : sug.suggestedValue;

    const optionalFields = [
      'lenderBank',
      'freeArea',
      'hasTerrace',
      'halfBaths',
    ];

    const isOptionalField = optionalFields.includes(item.field);
    if (!isOptionalField && (!valueToUse || valueToUse.trim() === '')) {
      alert('El valor no puede estar vacío');
      return;
    }

    dispatch(
      applyAutocompleteSuggestion({
        country,
        field: item.field,
        suggestedValue: valueToUse,
        model: sug.model,
        userFeedback: 100,
        suggestionId: sug?.suggestionId,
        initialConfidence: sug.initialConfidence,
        agentComment: comment || undefined,
      })
    );
    setShow(false);
  };

  return (
    <Card
      data-testid={`autocomplete-card-${item.field}`}
      className={`shadow-sm h-100 ${item.reviewed ? 'border-success' : ''}`}
    >
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <Card.Title className="mb-0">{title}</Card.Title>
          {item.reviewed && (
            <span style={{ fontFamily: 'Roboto' }} className="badge bg-success">
              Revisado ✓
            </span>
          )}
        </div>
        {item.suggestions?.length ? (
          <ListGroup variant="flush">
            {item.suggestions.map((sug, idx) => (
              <ListGroup.Item
                key={
                  sug.id || `${sug.source}-${sug.model}-${sug.suggestedValue}`
                }
                className="px-0"
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="me-2">
                    <div className="card-sub-title mb-1">
                      <strong>Valor:</strong>{' '}
                      {getFieldLabel(item.field, sug.suggestedValue)}
                    </div>
                    <div className="text-muted card-sub-title d-flex align-items-center gap-1">
                      <Badge className={SOURCE_LABELS[sug.source]?.className}>
                        {SOURCE_LABELS[sug.source]?.label}
                      </Badge>
                      · {MODEL_LABELS[sug.model]} · {sug.initialConfidence}%
                      {sug.sourceName ? ` · ${sug.sourceName}` : ''}
                      {sug.sourceType
                        ? ` · ${SOURCE_TYPE_LABELS[sug.sourceType]}`
                        : ''}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="btn-habi-white"
                    onClick={() => handleOpen(idx)}
                  >
                    Revisar
                  </Button>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <div className="text-muted">Sin sugerencias</div>
        )}
      </Card.Body>

      <Modal
        show={show}
        onHide={() => setShow(false)}
        centered
        size={docToView ? 'xl' : undefined}
        dialogClassName="modal-90w"
      >
        <WrapperModal>
          <Modal.Header closeButton>
            <Modal.Title>{title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              {docToView && (
                <Col md={8} className="border-start">
                  <FileViewer fileUrl={docToView} hideThumbnails />
                </Col>
              )}
              <Col md={docToView ? 4 : 12}>
                {selectedIdx != null && (
                  <div className="d-flex flex-column h-100 justify-content-between">
                    <div className="subtitle">
                      {item.fieldConfidenceLevel && (
                        <div className="mb-2">
                          <strong>Nivel de confianza:</strong>{' '}
                          <Badge
                            bg={getConfidenceBadgeVariant(
                              item.fieldConfidenceLevel
                            )}
                          >
                            {item.fieldConfidenceLevel}
                          </Badge>
                        </div>
                      )}
                      <div className="mb-2">
                        <strong>Modelo:</strong>{' '}
                        {MODEL_LABELS[item.suggestions[selectedIdx].model]}
                      </div>
                      <div className="mb-2">
                        <strong>Fuente:</strong>{' '}
                        <Badge
                          className={
                            SOURCE_LABELS[item.suggestions[selectedIdx].source]
                              ?.className
                          }
                        >
                          {
                            SOURCE_LABELS[item.suggestions[selectedIdx].source]
                              ?.label
                          }
                        </Badge>
                      </div>
                      <div className="mb-2">
                        <strong>Tipo de fuente:</strong>{' '}
                        {
                          SOURCE_TYPE_LABELS[
                            item.suggestions[selectedIdx].sourceType || ''
                          ]
                        }
                        <DocumentBySource
                          item={item}
                          selectedIdx={selectedIdx}
                          onSelectDoc={setDocToView}
                        />
                      </div>
                      {item.fieldConfidenceLevel === 'HIGH' ? (
                        <Form.Group controlId="editedValue" className="mb-3">
                          <Form.Label>Valor sugerido (editable):</Form.Label>
                          <EditableFieldByType
                            field={item.field}
                            config={getFieldConfig(item.field, country)}
                            value={editedValue}
                            onChange={setEditedValue}
                          />
                        </Form.Group>
                      ) : (
                        <div className="mb-3">
                          <strong>Valor sugerido:</strong>{' '}
                          {getFieldLabel(
                            item.field,
                            item.suggestions[selectedIdx].suggestedValue
                          )}
                        </div>
                      )}
                      <Form.Group controlId="agentComment" className="mt-3">
                        <Form.Label>Comentario (opcional)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={comment}
                          onChange={e => setComment(e.target.value)}
                          placeholder="Deja un comentario sobre la sugerencia"
                        />
                      </Form.Group>
                    </div>
                    <div
                      className={`d-flex px-0 py-2 border-top-0 ${
                        docToView ? 'flex-column' : 'flex-row gap-2'
                      }`}
                    >
                      <Button
                        className={`w-100 btn-habi ${docToView ? 'order-1 mb-2' : 'order-2'}`}
                        onClick={handleAccept}
                        data-testid={`accept-suggestion-${item.field}`}
                      >
                        Aceptar y autocompletar
                      </Button>
                      <Button
                        className={`w-100 btn-habi-white ${docToView ? 'order-2' : 'order-1'}`}
                        onClick={() => setShow(false)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </Modal.Body>
        </WrapperModal>
      </Modal>
    </Card>
  );
};
